import { Col, Form, Space, Table, Tag, Typography, theme } from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import DynamicForm from "../../controls/DynamicForm";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import {
  getWellbeingMaterials,
  getMaterialCoverage,
} from "../../repository/data.repository";
import {
  MaterialEngagementStatus,
  WellbeingMaterialCoverageRow,
  WellbeingMaterialCoverageSummary,
} from "../../repository/data.default";
import { InputObject } from "../../controls/default";
import { retry } from "../../utils/http";

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { readonly label: string; readonly color: string }> = {
  [MaterialEngagementStatus.NotStarted]: { label: "Not Started", color: "default" },
  [MaterialEngagementStatus.InProgress]: { label: "In Progress", color: "blue" },
  [MaterialEngagementStatus.Completed]: { label: "Completed", color: "green" },
};

// Engagement coverage for one material: pick a material → its assigned population (user · department ·
// company + sub-companies) with each user's lifecycle status (Not Started for assigned-but-untouched users)
// + a summary. Public / ad-hoc materials aren't tracked, so they show an empty roster.
function MaterialCoverageView() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<{ readonly material?: InputObject }>([], form);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<WellbeingMaterialCoverageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState<WellbeingMaterialCoverageSummary | undefined>(
    undefined,
  );
  const [loadedMaterial, setLoadedMaterial] = useState<string | undefined>(undefined);

  function load(materialUid: string, pageNumber: number): void {
    setLoading(true);
    retry(() =>
      getMaterialCoverage(materialUid, {
        searchQuery: { conditions: [] },
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    )
      .then((res) => {
        setRows(res.data);
        setTotal(res.totalRecords);
        setSummary(res.summary);
        setPage(pageNumber);
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
        setSummary(undefined);
        setLoading(false);
      });
  }

  function onChange(): void {
    const materialUid = form.getFieldValue("material")?.value;
    if (materialUid === loadedMaterial) return;
    setLoadedMaterial(materialUid);
    if (!materialUid) {
      setRows([]);
      setTotal(0);
      setSummary(undefined);
      setPage(0);
      return;
    }
    load(materialUid, 0);
  }

  return (
    <Col className="flex flex-auto flex-col overflow-hidden">
      <DynamicForm
        className="w-72"
        form={form}
        schema={{
          groups: [
            {
              controls: [
                {
                  type: "combobox",
                  name: "material",
                  label: "Material",
                  request: createSearchRequest(getWellbeingMaterials, "name"),
                },
              ],
            },
          ],
        }}
        onChange={onChange}
      />
      {summary && (
        <Space className="mb-2 mt-1 flex flex-row flex-wrap gap-1">
          <Tag>Total: {summary.total}</Tag>
          <Tag color="default">Not Started: {summary.notStarted}</Tag>
          <Tag color="blue">In Progress: {summary.inProgress}</Tag>
          <Tag color="green">Completed: {summary.completed}</Tag>
          <Tag color="geekblue">
            {Math.round(summary.completionRate * 100)}% complete
          </Tag>
        </Space>
      )}
      <Table
        className="mt-1 flex-auto"
        size="small"
        rowKey={(r: WellbeingMaterialCoverageRow) => r.userUid}
        loading={loading}
        dataSource={rows}
        scroll={{ y: 300 }}
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total,
          size: "small",
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (p) => {
            const materialUid = form.getFieldValue("material")?.value;
            if (materialUid) load(materialUid, p - 1);
          },
        }}
        locale={{
          emptyText: values?.material
            ? "No assigned population for this material (public/ad-hoc materials aren't tracked)."
            : "Pick a material to see its engagement coverage.",
        }}
        columns={[
          { title: "User", dataIndex: "userUserName", key: "userUserName", width: 180 },
          {
            title: "Company",
            dataIndex: "companyName",
            key: "companyName",
            width: 160,
            render: (v: string) => v ?? "-",
          },
          {
            title: "Department",
            dataIndex: "departmentName",
            key: "departmentName",
            width: 150,
            render: (v: string) => v ?? "-",
          },
          {
            title: "Required",
            dataIndex: "isRequired",
            key: "isRequired",
            width: 110,
            render: (v: boolean) => (
              <Tag color={v ? "red" : "default"}>{v ? "Required" : "Optional"}</Tag>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 130,
            render: (v: string) => {
              const meta = STATUS_META[v] ?? { label: v, color: "default" };
              return <Tag color={meta.color}>{meta.label}</Tag>;
            },
          },
          {
            title: "Started",
            dataIndex: "startedAt",
            key: "startedAt",
            width: 150,
            render: (v: string) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
          },
          {
            title: "Completed",
            dataIndex: "completedAt",
            key: "completedAt",
            width: 150,
            render: (v: string) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
          },
        ]}
      />
      <Typography.Text
        className="mt-1 text-xs"
        style={{ color: token.colorTextDescription }}
      >
        Coverage is the material's assigned population (user · department · company +
        sub-companies). Public / ad-hoc materials aren't tracked here.
      </Typography.Text>
    </Col>
  );
}

export default MaterialCoverageView;
